import {
  addUserRole,
  createUser,
  deleteUser,
  type SitePermissions,
  type UserResponse,
} from '@lms/api-client/backend';
import { z } from 'zod';

// SWR cache key for a site's user list. Mutating this after an add/remove is
// the SPA equivalent of the Next app's `revalidatePath('/[brand]/admin/users')`.
export const usersKey = (siteId: number) => ['admin/users', siteId] as const;

const createUserSchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  canManageGroups: z.boolean(),
  canManageUsers: z.boolean(),
});

export interface AddUserInput extends SitePermissions {
  name: string;
  email: string;
  siteId: number;
}

// Ports the `addUser` server action from _components/actions.ts. The backend
// handles both creating the user (if needed) and adding/updating the role.
// Every user with a row on the site can view it; the permission flags grant
// the write capabilities. Validation errors and backend failures propagate as
// thrown errors for the caller to surface; navigation/revalidation happen in
// the component via SWR.
export async function addUser(input: AddUserInput): Promise<UserResponse> {
  const { name, email, canManageGroups, canManageUsers } =
    createUserSchema.parse({
      name: input.name,
      email: input.email,
      canManageGroups: input.canManageGroups,
      canManageUsers: input.canManageUsers,
    });

  return createUser({
    name,
    email,
    siteId: input.siteId,
    canManageGroups,
    canManageUsers,
  });
}

// Ports the `removeUser` server action. The site id scopes the deletion to the
// current brand, mirroring the action's `getSiteBySlug` lookup.
export async function removeUser(id: number, siteId?: number): Promise<void> {
  await deleteUser(id, siteId);
}

// Replace a user's permissions at a site. The backend upserts the user's row,
// so this only ever changes the flags, never their access to the site.
export async function setUserPermissions(
  userId: number,
  siteId: number,
  permissions: SitePermissions,
): Promise<UserResponse> {
  return addUserRole(userId, { siteId, ...permissions });
}
